/* eslint-disable react/no-unescaped-entities */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, FileText, Download, Link as LinkIcon, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { trackPurchase } from '@/lib/analytics';
import SEO from '@/components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Premium confirmation page shown after Stripe redirects back to Hérita.
 * It polls /api/payments/status/{session_id} until payment_status === 'paid'
 * (Stripe's webhook + the poll both flip the dossier server-side; whoever
 * fires first wins, the other one is a no-op).
 *
 * The page provides three actions:
 *   - "Ouvrir mon Dossier Hérita"     → /dashboard?dossier={id}
 *   - "Télécharger mon Dossier PDF"   → /api/analyses/{id}/dossier.pdf
 *   - "Copier le lien de mon dossier" → clipboard copy
 */
const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const dossierIdParam = params.get('dossier');

  const [state, setState] = useState({
    phase: 'verifying', // verifying | paid | error | expired
    dossierId: dossierIdParam || null,
    amountTotal: null, // raw Stripe amount_total (in cents)
    currency: null,
    error: null,
    attempts: 0,
  });

  // Track which transaction_ids we've already pushed to GA4 within this
  // page mount, so that React Strict-Mode double-effects or quick re-renders
  // never fire a duplicate purchase event (localStorage dedupe handles
  // cross-mount reloads — this ref handles within-mount dupes).
  const purchaseFiredRef = useRef(null);

  // Poll the backend for payment status
  useEffect(() => {
    if (!sessionId) {
      setState((s) => ({ ...s, phase: 'error', error: 'Session de paiement absente.' }));
      return undefined;
    }

    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 8; // 8 × 1.8s ≈ 14s

    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === 'paid') {
          setState({
            phase: 'paid',
            dossierId: data.dossier_id || dossierIdParam,
            amountTotal: typeof data.amount_total === 'number' ? data.amount_total : null,
            currency: data.currency || null,
            error: null,
            attempts,
          });
          return;
        }
        if (data.status === 'expired') {
          setState({
            phase: 'expired',
            dossierId: data.dossier_id || dossierIdParam,
            amountTotal: null,
            currency: null,
            error: 'La session de paiement a expiré.',
            attempts,
          });
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setState({
            phase: 'error',
            dossierId: data.dossier_id || dossierIdParam,
            amountTotal: null,
            currency: null,
            error:
              'Le paiement est en cours de validation. Rafraîchissez la page dans quelques secondes.',
            attempts,
          });
          return;
        }
        setTimeout(tick, 1800);
      } catch (e) {
        if (cancelled) return;
        if (attempts >= MAX_ATTEMPTS) {
          setState({
            phase: 'error',
            dossierId: dossierIdParam,
            amountTotal: null,
            currency: null,
            error:
              e?.response?.data?.detail ||
              'Vérification impossible pour le moment. Réessayez dans un instant.',
            attempts,
          });
          return;
        }
        setTimeout(tick, 1800);
      }
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [sessionId, dossierIdParam]);

  const dossierLink = state.dossierId
    ? `${window.location.origin}/dashboard?dossier=${state.dossierId}`
    : null;

  // GA4 — purchase, fired exactly ONCE per Stripe transaction.
  // Triple-protection against duplicates:
  //   1. We only enter the branch when state.phase === 'paid'
  //   2. purchaseFiredRef guards against Strict-Mode / re-render dupes
  //   3. trackPurchase itself reads localStorage to guard against reloads
  // The amount comes from Stripe (`amount_total` is in the smallest currency
  // unit, i.e. cents for EUR) so GA4 receives the real paid value, not a
  // hardcoded one.
  useEffect(() => {
    if (state.phase !== 'paid' || !sessionId) return;
    if (purchaseFiredRef.current === sessionId) return;

    const value =
      typeof state.amountTotal === 'number' && state.amountTotal > 0
        ? state.amountTotal / 100
        : 19;
    const currency = (state.currency || 'EUR').toUpperCase();

    const fired = trackPurchase({
      sessionId,
      documentId: state.dossierId,
      value,
      currency,
      packageId: 'herita_dossier_premium',
    });
    if (fired) {
      purchaseFiredRef.current = sessionId;
    }
  }, [state.phase, state.amountTotal, state.currency, state.dossierId, sessionId]);

  const openDossier = async () => {
    if (!state.dossierId) return;
    try {
      const { data } = await axios.get(`${API}/analyses/${state.dossierId}`);
      // Push the analysis into history.state so DashboardPageV2 picks it
      // up like a normal post-upload navigation, then navigate.
      navigate('/dashboard', {
        state: {
          analysis: data,
          document: {
            id: data.document_id,
            filename: data.document_filename || 'document.pdf',
          },
        },
      });
    } catch (e) {
      toast.error("Impossible d'ouvrir le dossier", {
        description: e?.response?.data?.detail || 'Réessayez dans un instant.',
      });
    }
  };

  const downloadPdf = () => {
    if (!state.dossierId) return;
    const url = `${API}/analyses/${state.dossierId}/dossier.pdf`;
    const a = document.createElement('a');
    a.href = url;
    a.download = 'Dossier-Herita.pdf';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const copyLink = async () => {
    if (!dossierLink) return;
    try {
      await navigator.clipboard.writeText(dossierLink);
      toast.success('Lien copié dans le presse-papiers');
    } catch {
      // Fallback: select & manually copy
      const ta = document.createElement('textarea');
      ta.value = dossierLink;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        toast.success('Lien copié dans le presse-papiers');
      } catch {
        toast.error('Impossible de copier le lien automatiquement.');
      }
      document.body.removeChild(ta);
    }
  };

  // -------- Render --------
  return (
    <div
      className="min-h-screen bg-[#F9F7F3] text-[#2C363F] flex flex-col"
      data-testid="page-paiement-succes"
    >
      <SEO
        title="Paiement confirmé | HÉRITA"
        description="Votre Dossier Hérita est débloqué."
        path="/paiement-succes"
        noindex
      />
      <header className="border-b border-[#E2DBCF] bg-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 py-5 flex items-center justify-between">
          <Link
            to="/"
            className="font-['IBM_Plex_Mono'] text-[11px] sm:text-[12px] uppercase tracking-[0.28em] text-[#1A2530] hover:opacity-80 transition-opacity"
            data-testid="success-brand-link"
          >
            HÉRITA
          </Link>
          <Link
            to="/"
            className="inline-flex items-center font-['Work_Sans'] text-[12px] sm:text-[13px] text-[#2C363F] hover:text-[#1A2530] transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5 mr-2" />
            Accueil
          </Link>
        </div>
      </header>

      <main className="flex-1 px-5 sm:px-8 lg:px-12 py-16 sm:py-24">
        <div className="max-w-2xl mx-auto">
          {state.phase === 'verifying' && (
            <div
              className="bg-white border border-[#E2DBCF] rounded-2xl p-8 sm:p-12 text-center"
              data-testid="success-verifying"
            >
              <div className="w-12 h-12 mx-auto mb-6 rounded-full border-2 border-[#E2DBCF] border-t-[#1A2530] animate-spin" />
              <p className="font-['IBM_Plex_Mono'] text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-[#2C363F] opacity-80 mb-3">
                Vérification du paiement
              </p>
              <h1 className="font-['Cormorant_Garamond'] font-medium text-[28px] sm:text-[36px] text-[#1A2530] leading-tight">
                Nous finalisons votre paiement avec Stripe.
              </h1>
              <p className="font-['Work_Sans'] text-[14px] sm:text-[15px] text-[#2C363F] mt-4 leading-relaxed">
                Cela ne prend que quelques secondes. Merci de ne pas fermer cette page.
              </p>
            </div>
          )}

          {state.phase === 'paid' && (
            <div data-testid="success-paid">
              {/* Hero confirmation card */}
              <div className="bg-white border border-[#E2DBCF] rounded-2xl p-8 sm:p-12 mb-6">
                <div className="flex items-center justify-center mb-6">
                  <div className="w-14 h-14 rounded-full bg-[#F0F4EE] border border-[#2D6A4F]/30 flex items-center justify-center">
                    <CheckCircle2 className="w-7 h-7 text-[#2D6A4F]" />
                  </div>
                </div>
                <p className="font-['IBM_Plex_Mono'] text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-[#1A2530] text-center mb-3">
                  Paiement confirmé
                </p>
                <h1 className="font-['Cormorant_Garamond'] font-medium text-[32px] sm:text-[44px] text-[#1A2530] leading-[1.1] text-center mb-4">
                  Votre Dossier Hérita est débloqué.
                </h1>
                {/* Trust chip — sober green pill confirming Stripe verification */}
                <div className="flex justify-center mb-5">
                  <div
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#F0F4EE] border border-[#2D6A4F]/30"
                    data-testid="success-stripe-chip"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 text-[#2D6A4F]" />
                    <span className="font-['Work_Sans'] text-[12px] sm:text-[12.5px] font-medium text-[#2D6A4F] leading-none">
                      Paiement vérifié par Stripe
                    </span>
                  </div>
                </div>
                <p className="font-['Work_Sans'] text-[15px] sm:text-[16px] text-[#2C363F] leading-relaxed max-w-lg mx-auto text-center">
                  Vous accédez désormais à votre lecture personnalisée, à votre dossier
                  complet et à l'export PDF, depuis n'importe quel appareil, sans
                  jamais repayer pour ce document.
                </p>
              </div>

              {/* Two primary actions */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <button
                  type="button"
                  onClick={openDossier}
                  className="font-['Work_Sans'] text-[14px] sm:text-[15px] font-semibold bg-[#1A2530] hover:bg-[#2C363F] text-white rounded-full px-6 py-3.5 transition-colors inline-flex items-center justify-center gap-2"
                  data-testid="success-open-dossier"
                >
                  <FileText className="h-4 w-4" />
                  Ouvrir mon Dossier Hérita
                </button>
                <button
                  type="button"
                  onClick={downloadPdf}
                  className="font-['Work_Sans'] text-[14px] sm:text-[15px] font-semibold bg-white hover:bg-[#F0EBE1] text-[#1A2530] border border-[#1A2530] rounded-full px-6 py-3.5 transition-colors inline-flex items-center justify-center gap-2"
                  data-testid="success-download-pdf"
                >
                  <Download className="h-4 w-4" />
                  Télécharger mon Dossier PDF
                </button>
              </div>

              {/* Secondary action */}
              <button
                type="button"
                onClick={copyLink}
                className="w-full font-['Work_Sans'] text-[13px] text-[#2C363F] hover:text-[#1A2530] underline underline-offset-2 inline-flex items-center justify-center gap-2 py-2"
                data-testid="success-copy-link"
              >
                <LinkIcon className="h-3.5 w-3.5" />
                Copier le lien de mon dossier
              </button>

              <p className="font-['Work_Sans'] text-[12px] italic text-[#2C363F] opacity-80 text-center mt-8 leading-relaxed">
                Votre dossier reste accessible et vous ne repayerez jamais pour ce
                document. Conservez ce lien : il vous suffit à le retrouver à
                tout moment.
              </p>
            </div>
          )}

          {(state.phase === 'error' || state.phase === 'expired') && (
            <div
              className="bg-white border border-[#E2DBCF] rounded-2xl p-8 sm:p-12 text-center"
              data-testid="success-error"
            >
              <p className="font-['IBM_Plex_Mono'] text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-[#2C363F] opacity-80 mb-3">
                Vérification en cours
              </p>
              <h1 className="font-['Cormorant_Garamond'] font-medium text-[28px] sm:text-[36px] text-[#1A2530] leading-tight mb-4">
                Nous n'avons pas encore confirmé votre paiement.
              </h1>
              <p className="font-['Work_Sans'] text-[14px] sm:text-[15px] text-[#2C363F] leading-relaxed mb-6">
                {state.error}
              </p>
              {state.dossierId && (
                <button
                  type="button"
                  onClick={openDossier}
                  className="font-['Work_Sans'] text-[14px] font-semibold bg-[#1A2530] hover:bg-[#2C363F] text-white rounded-full px-6 py-3 transition-colors inline-flex items-center justify-center gap-2"
                  data-testid="success-retry-open"
                >
                  Retourner à mon dossier
                </button>
              )}
            </div>
          )}
        </div>
      </main>

      <footer className="border-t border-[#E2DBCF] bg-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12 py-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="font-['Work_Sans'] text-sm text-[#2C363F]">
            © 2026 Hérita. Tous droits réservés.
          </p>
          <div className="flex flex-wrap gap-x-5 gap-y-2 justify-center">
            <Link to="/mentions-legales" className="font-['Work_Sans'] text-sm text-[#2C363F] hover:text-[#1A2530] transition-colors">Mentions légales</Link>
            <Link to="/confidentialite" className="font-['Work_Sans'] text-sm text-[#2C363F] hover:text-[#1A2530] transition-colors">Confidentialité</Link>
            <Link to="/cgu" className="font-['Work_Sans'] text-sm text-[#2C363F] hover:text-[#1A2530] transition-colors">CGU</Link>
            <Link to="/contact" className="font-['Work_Sans'] text-sm text-[#2C363F] hover:text-[#1A2530] transition-colors">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PaymentSuccessPage;
